import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'

// Espelha a uniao de cores de LabelBadge.vue (nao importavel como type de um .vue em .ts puro).
type LabelBadgeColor = 'blue' | 'orange' | 'green' | 'yellow' | 'red' | 'gray'

/**
 * Mapeamentos compartilhados de status e utilitarios da area de Medicos.
 * Centraliza o que antes estava duplicado em DoctorHeader.vue e PendingDoctor.vue.
 */

type StatusConfig = { label: string; color: LabelBadgeColor }

const INVITE_STATUS_MAP: Record<string, StatusConfig> = {
  [CompanyDoctorInviteStatusEnum.PENDING]: { label: 'Pendente', color: 'yellow' },
  [CompanyDoctorInviteStatusEnum.ACCEPTED]: { label: 'Aceito', color: 'green' },
  [CompanyDoctorInviteStatusEnum.DECLINED]: { label: 'Recusado', color: 'red' },
  [CompanyDoctorInviteStatusEnum.EXPIRED]: { label: 'Expirado', color: 'gray' },
  [CompanyDoctorInviteStatusEnum.CANCELLED]: { label: 'Cancelado', color: 'gray' }
}

const CRM_STATUS_MAP: Record<string, StatusConfig> = {
  Ativo: { label: 'Ativo', color: 'green' },
  Cancelado: { label: 'Cancelado', color: 'red' },
  Transferido: { label: 'Transferido', color: 'orange' },
  Falecido: { label: 'Falecido', color: 'gray' }
}

/** Traduz o status do vinculo (companyStatus/doctorStatus) e devolve label + cor do badge. */
export function getInviteStatusConfig(status?: string | null): StatusConfig | null {
  if (!status) return null
  return INVITE_STATUS_MAP[status] ?? { label: status, color: 'gray' }
}

/** Traduz o status do CRM e devolve label + cor do badge. */
export function getCrmStatusConfig(status?: string | null): StatusConfig | null {
  if (!status) return null
  return CRM_STATUS_MAP[status] ?? { label: status, color: 'gray' }
}

export type InviteSummaryTone = 'positive' | 'warning' | 'negative' | 'neutral'

type InviteStatuses = { companyStatus: string; doctorStatus: string } | null | undefined

const IS = CompanyDoctorInviteStatusEnum

/**
 * Resume os dois lados do convite (empresa e medico) numa frase so, para o card de vinculo.
 * Ordem: encerrado (cancelado/expirado) > recusado > aceito > aguardando.
 */
export function getInviteSummary(invite: InviteStatuses): {
  label: string
  tone: InviteSummaryTone
} {
  if (!invite) return { label: 'Sem convite', tone: 'neutral' }
  const { companyStatus, doctorStatus } = invite
  const either = (status: string) => companyStatus === status || doctorStatus === status

  if (either(IS.CANCELLED)) return { label: 'Convite cancelado', tone: 'neutral' }
  if (either(IS.EXPIRED)) return { label: 'Convite expirado', tone: 'neutral' }
  if (doctorStatus === IS.DECLINED) return { label: 'Recusado pelo médico', tone: 'negative' }
  if (companyStatus === IS.DECLINED) return { label: 'Recusado pela empresa', tone: 'negative' }
  if (companyStatus === IS.ACCEPTED && doctorStatus === IS.ACCEPTED) {
    return { label: 'Convite aceito', tone: 'positive' }
  }
  if (doctorStatus === IS.PENDING) return { label: 'Aguardando aceite do médico', tone: 'warning' }
  if (companyStatus === IS.PENDING) {
    return { label: 'Aguardando aprovação da empresa', tone: 'warning' }
  }
  return { label: 'Convite em andamento', tone: 'neutral' }
}

/** Duas primeiras letras do nome, em maiusculas, para fallback de avatar. */
export function getInitials(name: string): string {
  return (name || '').substring(0, 2).toUpperCase() || 'MD'
}

/**
 * Normaliza nomes para Title Case (ex.: "JOAO DA SILVA" -> "Joao Da Silva").
 * `capitalize` do CSS nao rebaixa letras ja em maiuscula; por isso fazemos em JS.
 */
export function toTitleCase(value?: string | null): string {
  return (value || '').toLowerCase().replace(/(^|[\s'-])(\p{L})/gu, (_, sep, letter) => {
    return `${sep}${letter.toUpperCase()}`
  })
}

/**
 * Resolve a URL do avatar: prefixa URLs relativas com a base da API
 * (mesmo tratamento do fix de avatar em Header.vue / Profile).
 */
export function resolveAvatarUrl(url?: string | null): string | null {
  if (!url) return null
  const base = process.env.VUE_APP_API_URL
  if (url.startsWith('/') && base) return `${base}${url}`
  return url
}
